import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { format } from "date-fns";

interface Props {
  invoiceData: any;
  /** Optional array of grouped invoices for a common receipt */
  groupedInvoices?: any[];
  studentData: any;
  onClose: () => void;
}

const ReceiptModal: React.FC<Props> = ({ invoiceData, groupedInvoices, studentData, onClose }) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Determine if this is a grouped receipt
  const isGrouped = groupedInvoices && groupedInvoices.length > 1;
  const invoices = isGrouped ? groupedInvoices : [invoiceData];

  // Helper to get total paid amount for an invoice
  const getInvoicePaid = (inv: any) => {
    if (inv?.paidAmount !== undefined && Number(inv.paidAmount) > 0) return Number(inv.paidAmount);
    if (!inv?.student_payments || inv.student_payments.length === 0) {
      return inv?.status === "completed" ? Math.max(0, Number(inv.amount || 0) - Number(inv.discount || 0)) : 0;
    }
    return inv.student_payments.reduce(
      (sum: number, p: any) =>
        p.payment_status === "completed" || p.payment_status === "approved"
          ? sum + Number(p.amount || 0)
          : sum,
      0
    );
  };

  // Calculate totals across all invoices in the receipt
  const totalAmount = invoices!.reduce((sum: number, inv: any) => sum + Number(inv.amount || 0), 0);
  const totalDiscount = invoices!.reduce((sum: number, inv: any) => sum + Number(inv.discount || 0), 0);
  const netPayable = Math.max(0, totalAmount - totalDiscount);
  const totalPaid = invoices!.reduce((sum: number, inv: any) => sum + getInvoicePaid(inv), 0);
  const balanceDue = Math.max(0, netPayable - totalPaid);

  const isCompleted = balanceDue === 0 && totalPaid > 0;
  const isPartial = balanceDue > 0 && totalPaid > 0;

  // Build reference string
  const refString = isGrouped
    ? groupedInvoices!.map((inv: any) => inv.invoice_number || inv.reference).join(', ')
    : invoiceData?.invoice_number || invoiceData?.reference;

  const handleDownload = async () => {
    if (!receiptRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      const element = receiptRef.current;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: 1024,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById("printable-receipt");
          if (clonedElement) {
            // Enforce standard desktop receipt width
            clonedElement.style.width = "750px";
            clonedElement.style.maxWidth = "750px";
            clonedElement.style.minWidth = "750px";
            clonedElement.style.margin = "0 auto";
            clonedElement.style.padding = "32px";
            clonedElement.style.boxSizing = "border-box";

            // Ensure parent hierarchy in clonedDoc does not restrict width or clip
            let current: HTMLElement | null = clonedElement.parentElement;
            while (current && current !== clonedDoc.body) {
              current.style.width = "800px";
              current.style.maxWidth = "none";
              current.style.minWidth = "800px";
              current.style.padding = "0";
              current.style.margin = "0 auto";
              current.style.overflow = "visible";
              current = current.parentElement;
            }

            // Ensure table responsive doesn't clip content
            clonedElement.querySelectorAll(".table-responsive").forEach((t) => {
              (t as HTMLElement).style.overflow = "visible";
              (t as HTMLElement).style.width = "100%";
            });
          }
        },
      });

      const imgData = canvas.toDataURL("image/png", 1.0);

      // Create PDF in A4 format (mm units)
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      const contentWidth = pageWidth - margin * 2; // 190mm
      const contentHeight = pageHeight - margin * 2; // 277mm

      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight <= contentHeight) {
        // Fits comfortably on a single page
        pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight);
      } else if (imgHeight <= contentHeight * 1.15) {
        // Slightly taller than single page: scale proportionally to fit cleanly on one page
        const scaledHeight = contentHeight;
        const scaledWidth = (canvas.width * scaledHeight) / canvas.height;
        const xOffset = margin + (contentWidth - scaledWidth) / 2;
        pdf.addImage(imgData, "PNG", xOffset, margin, scaledWidth, scaledHeight);
      } else {
        // Multi-page slicing for large receipts
        let heightLeft = imgHeight;
        let position = margin;

        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
        heightLeft -= contentHeight;

        while (heightLeft > 0) {
          position = margin - (imgHeight - heightLeft);
          pdf.addPage();
          pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
          heightLeft -= contentHeight;
        }
      }

      const receiptNum = invoices![0]?.invoice_number || invoices![0]?.reference || "payment";
      pdf.save(`Receipt_${receiptNum}.pdf`);
    } catch (err) {
      console.error("Error generating PDF", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!invoiceData && (!groupedInvoices || groupedInvoices.length === 0)) return null;

  // Use the first invoice for shared details
  const primaryInvoice = invoices![0];

  const parentName =
    studentData?.profile?.fathers_name ||
    studentData?.fathers_name ||
    primaryInvoice?.student?.profile?.fathers_name ||
    primaryInvoice?.student?.fathers_name;

  // Resolve Center Information from primaryInvoice or studentData
  const center = primaryInvoice?.center || studentData?.center;
  const centerName =
    center?.name ||
    studentData?.center_name ||
    studentData?.center?.name ||
    "";

  const centerPhone =
    center?.phone ||
    studentData?.center?.phone ||
    studentData?.center_phone ||
    "";

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
      <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1051 }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="modal-title">Payment Receipt</h5>
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-primary btn-sm me-2 d-flex align-items-center"
                  onClick={handleDownload}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-download me-1" />
                      Download PDF
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={onClose}
                  disabled={isDownloading}
                >
                  <i className="ti ti-x me-1" />
                  Close
                </button>
              </div>
            </div>
            <div className="modal-body p-4" style={{ backgroundColor: "#f8f9fa" }}>
              <div
                ref={receiptRef}
                id="printable-receipt"
                className="bg-white p-4 border rounded shadow-sm"
                style={{ width: "100%", margin: "0 auto", position: "relative" }}
              >
                {/* Header with Logo and Center Info */}
                <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-4">
                  <div className="d-flex flex-column">
                    <img
                      src="/assets/img/logo.png"
                      alt="Aloha Logo"
                      style={{ height: "42px", width: "fit-content" }}
                      className="mb-2"
                    />
                    {centerName && (
                      <div className="mt-1">
                        <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: "1.05rem" }}>
                          {centerName}
                        </h6>
                        {centerPhone && (
                          <p className="text-muted mb-0 small" style={{ fontSize: "0.82rem" }}>
                            <strong>Contact:</strong> {centerPhone}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-end">
                    <h2 className="mb-1 text-primary fw-bold">RECEIPT</h2>
                    <p className="text-muted mb-0">Ref: {refString}</p>
                    <p className="text-muted mb-0">
                      Date: {format(new Date(), "dd MMM yyyy")}
                    </p>
                  </div>
                </div>

                {/* Info Sections */}
                <div className="row mb-4">
                  <div className="col-6">
                    <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                      Billed To:
                    </h6>
                    <p className="mb-0 fw-bold text-muted">{studentData?.name}</p>
                    {parentName && (
                      <p className="mb-0 text-muted">
                        Parentage: <span className="text-muted">{parentName}</span>
                      </p>
                    )}
                    <p className="mb-0 text-muted">ID: {studentData?.profile?.studentid || 'N/A'}</p>
                    <p className="mb-0 text-muted">Course: {primaryInvoice?.courseName}</p>
                  </div>
                  <div className="col-6 text-end">
                    <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                      Payment Details:
                    </h6>
                    <p className="mb-0 text-muted">Invoice Date: {primaryInvoice?.invoiceDate}</p>
                    {!isGrouped && (
                      <p className="mb-0 text-muted">Stage: {primaryInvoice?.stageName}</p>
                    )}
                    <p className={`mb-0 fw-bold ${isCompleted ? 'text-success' : isPartial ? 'text-info' : 'text-warning'}`}>
                      Status: {isCompleted ? 'Paid' : isPartial ? 'Partially Paid' : 'Pending'}
                    </p>
                  </div>
                </div>

                {/* Amount Table */}
                <div className="table-responsive mb-4">
                  <table className="table table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Description</th>
                        <th className="text-end">Fee Amount</th>
                        <th className="text-end">Paid Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices!.map((inv: any, idx: number) => {
                        const invPaid = getInvoicePaid(inv);
                        return (
                          <tr key={idx}>
                            <td>
                              {inv.type === 'registrationfee' ? 'Registration Fee' : 'Course Fee'}
                              {' - '}
                              {inv.stageName}
                              {inv.discount > 0 && (
                                <small className="text-muted d-block">
                                  Discount: -₹{Number(inv.discount).toLocaleString()}
                                </small>
                              )}
                            </td>
                            <td className="text-end">₹{Number(inv.amount).toLocaleString()}</td>
                            <td className="text-end font-monospace">₹{invPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                        );
                      })}
                      {totalDiscount > 0 && (
                        <tr>
                          <td>Total Discount</td>
                          <td className="text-end text-danger" colSpan={2}>-₹{totalDiscount.toLocaleString()}</td>
                        </tr>
                      )}
                      <tr className="table-active fw-bold">
                        <td>Total Amount Paid</td>
                        <td className="text-end text-success" colSpan={2}>
                          ₹{totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                      {balanceDue > 0 && (
                        <tr className="table-warning fw-bold">
                          <td>Remaining Balance Due</td>
                          <td className="text-end text-danger" colSpan={2}>
                            ₹{balanceDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Remarks */}
                {primaryInvoice?.offlineRemarks && (
                  <div
                    className="mb-4 p-3 rounded"
                    style={{ background: "#f8f9fa", borderLeft: "4px solid #6c757d" }}
                  >
                    <h6 className="text-uppercase text-muted mb-1" style={{ fontSize: "0.7rem", letterSpacing: "0.08em" }}>
                      Remarks
                    </h6>
                    <p className="mb-0" style={{ fontSize: "0.9rem", color: "#343a40", whiteSpace: "pre-wrap" }}>
                      {primaryInvoice.offlineRemarks}
                    </p>
                  </div>
                )}

                {/* Footer Message */}
                <div className="text-center mt-4 pt-3 border-top">
                  <p className="text-muted small fw-medium mb-1">
                    <span className="text-danger fw-semibold">* Note:</span> Fee once paid is non-refundable and non-transferable under any circumstances.
                  </p>
                  <p className="text-muted mb-0">Thank you for your payment!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReceiptModal;
